
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InventoryItem } from "./inventoryTypes";
import { fetchEmployees, useInventoryOperations } from "./inventoryService";

interface AssignItemDialogProps {
  item: InventoryItem;
  open: boolean;
  onClose: (success: boolean) => void;
}

export function AssignItemDialog({ item, open, onClose }: AssignItemDialogProps) {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const { handleItemStatusUpdate } = useInventoryOperations();
  
  const { data: employees = [], isLoading } = useQuery({
    queryKey: ["employees"],
    queryFn: fetchEmployees,
  });

  const handleAssign = async () => {
    if (!selectedEmployeeId) return;
    
    const success = await handleItemStatusUpdate(
      item.id,
      "Assigned",
      selectedEmployeeId
    );
    
    onClose(success);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Item</DialogTitle>
          <DialogDescription>
            Assign {item.serial} to an employee
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="employee" className="text-sm font-medium">
              Select Employee
            </label>
            
            {isLoading ? (
              <div className="h-10 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <Select 
                value={selectedEmployeeId?.toString() || ""} 
                onValueChange={(value) => setSelectedEmployeeId(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id.toString()}>
                      {employee.name} ({employee.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssign}
            disabled={!selectedEmployeeId || isLoading}
          >
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
